import { z } from "zod";
/**
 * What a product's results reveal shows: an ordered list of blocks, each a
 * question drawn as one chart, or the room's quotes. A round stores its own
 * list, or none for the defaults of its look; either way it is read through
 * `resolveRevealBlocks`, which keeps it in step with the questions as they
 * change. A question can be drawn more than once, as different charts; it is
 * never left out, only hidden, so a new question always gets its defaults.
 */
export const REVEAL_CHARTS = [
    "ranking",
    "dots",
    "top_box",
    "head_to_head",
    "profile",
    "count",
    "bars",
    "heatmap",
    "tiles",
    "podium",
];
export const revealBlockSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("question"),
        question_id: z.number().int(),
        chart: z.enum(REVEAL_CHARTS),
        hidden: z.boolean(),
    }),
    z.object({ kind: z.literal("quotes"), hidden: z.boolean() }),
]);
export const revealBlocksSchema = z.array(revealBlockSchema).max(200);
/** A block's identity in a list: a question is drawn as each chart at most once. */
export function revealBlockKey(block) {
    return block.kind === "quotes" ? "quotes" : `${block.question_id}:${block.chart}`;
}
/** The scale question samples are ranked by: the one marked as headline, else the first. */
export function headlineQuestion(questions) {
    const perSample = questions.filter((q) => q.test_type !== "general" && q.question_type === "scale");
    return perSample.find((q) => q.is_headline) ?? perSample[0] ?? null;
}
/** The charts a question can be drawn as; none for written answers, which go to the quotes. */
export function chartsFor(question, ctx) {
    const perSample = question.test_type !== "general";
    switch (question.question_type) {
        case "scale":
            // A scale asked once for the whole product has nothing to compare.
            return perSample
                ? ["ranking", "dots", "top_box", ...(ctx.headToHead ? ["head_to_head"] : []), "profile"]
                : [];
        case "yes_no":
            return ["count", "bars"];
        case "single_choice":
            return ["bars"];
        case "multi_choice":
            return perSample ? ["heatmap", "bars"] : ["bars"];
        case "sample_pick":
            return ["tiles"];
        case "sample_rank":
            return ["podium"];
        default:
            return [];
    }
}
/**
 * Where a question's default blocks go: the headline first, then the yes/no
 * verdicts, the favourite, the choices, the profile, and the quotes last.
 */
function defaultPlace(question, headlineId) {
    if (question.id === headlineId)
        return 0;
    switch (question.question_type) {
        case "yes_no":
            return 1;
        case "sample_pick":
        case "sample_rank":
            return 2;
        case "single_choice":
        case "multi_choice":
            return 3;
        default:
            return 4;
    }
}
/**
 * A question's blocks before anyone changes them. A showcase is for investors:
 * the ranking and the verdicts, with the detail hidden. An everyday tasting is
 * for R&D: every taster's score and every chart.
 */
function defaultBlocksFor(question, ctx, headlineId) {
    const charts = chartsFor(question, ctx);
    if (charts.length === 0)
        return [];
    const showcase = ctx.look === "showcase";
    const block = (chart, hidden = false) => ({
        kind: "question",
        question_id: question.id,
        chart,
        hidden,
    });
    if (question.question_type === "scale") {
        if (question.id !== headlineId)
            return [block("profile", showcase)];
        return [block(showcase ? "ranking" : "dots"), ...(charts.includes("head_to_head") ? [block("head_to_head")] : [])];
    }
    if (question.question_type === "multi_choice" && charts[0] === "heatmap")
        return [block("heatmap", showcase)];
    return [block(charts[0])];
}
export function defaultRevealBlocks(ctx) {
    const headlineId = headlineQuestion(ctx.questions)?.id ?? null;
    const placed = ctx.questions
        .map((q, order) => ({ place: defaultPlace(q, headlineId), order, blocks: defaultBlocksFor(q, ctx, headlineId) }))
        .sort((a, b) => a.place - b.place || a.order - b.order);
    return [...placed.flatMap((p) => p.blocks), { kind: "quotes", hidden: false }];
}
/**
 * A round's reveal as it stands: the stored list without blocks for questions
 * that are gone or charts that no longer fit, each question it does not draw
 * added with its defaults before the quotes, and the quotes exactly once.
 */
export function resolveRevealBlocks(stored, ctx) {
    const parsed = revealBlocksSchema.safeParse(stored);
    if (stored == null || !parsed.success)
        return defaultRevealBlocks(ctx);
    const byId = new Map(ctx.questions.map((q) => [q.id, q]));
    const seen = new Set();
    const kept = [];
    for (const block of parsed.data) {
        const key = revealBlockKey(block);
        if (seen.has(key))
            continue;
        if (block.kind === "question") {
            const question = byId.get(block.question_id);
            if (!question || !chartsFor(question, ctx).includes(block.chart))
                continue;
        }
        seen.add(key);
        kept.push(block);
    }
    const drawn = new Set(kept.flatMap((b) => (b.kind === "question" ? [b.question_id] : [])));
    const headlineId = headlineQuestion(ctx.questions)?.id ?? null;
    const missing = ctx.questions.filter((q) => !drawn.has(q.id)).flatMap((q) => defaultBlocksFor(q, ctx, headlineId));
    const quotesAt = kept.findIndex((b) => b.kind === "quotes");
    if (quotesAt === -1)
        return [...kept, ...missing, { kind: "quotes", hidden: false }];
    return [...kept.slice(0, quotesAt), ...missing, ...kept.slice(quotesAt)];
}
/** The lowest score that counts as a top score: the top quarter of the scale, so 7–9 of 9 and 8–10 of 10. */
export function topBoxFrom(min, max) {
    return max - Math.floor((max - min) / 4);
}
/** Whether the room sees a written answer: the host's decision, else the look's default. */
export function quoteShown(decision, look) {
    return decision ?? look === "everyday";
}
//# sourceMappingURL=tasting-reveal.js.map